import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Search,
  Users,
  Download,
  Star,
  Award,
  TrendingUp,
  Clock,
  Filter,
  ChevronDown,
  User,
} from 'lucide-react';
import { useSequencerStats } from '@/hooks/useSequencerStats';
import { useAllSequencers } from '@/hooks/useAllSequencers';
import { SequencerCard } from '@/components/sequences/SequencerCard';
import { cn } from '@/lib/utils';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';

/**
 * Main Sequencers Discovery Page
 *
 * Features:
 * - Hero section with search and statistics
 * - Multiple discovery sections (Featured, Top, Most Downloaded, New Creators)
 * - Filtering by holiday, software, genre, and free-only creators
 * - Search functionality by creator name
 */
export default function SequencersCatalog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterFeatured, setFilterFeatured] = useState(false);
  const [filterVerified, setFilterVerified] = useState(false);
  const [filterFreeOnly, setFilterFreeOnly] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<string>('');
  const [selectedSoftware, setSelectedSoftware] = useState<string>('');
  const [selectedGenre, setSelectedGenre] = useState<string>('');

  // Fetch statistics for hero section
  const { stats, isLoading: statsLoading } = useSequencerStats();

  // Fetch featured sequencers
  const { sequencers: featuredSequencers, isLoading: featuredLoading } = useAllSequencers({
    filterFeatured: true,
    sortBy: 'sequences',
    limit: 8,
  });

  // Fetch top sequencers (by sequence count)
  const { sequencers: topSequencers, isLoading: topLoading } = useAllSequencers({
    sortBy: 'sequences',
    limit: 12,
  });

  // Fetch most downloaded creators
  const { sequencers: downloadedSequencers, isLoading: downloadedLoading } = useAllSequencers({
    sortBy: 'downloads',
    limit: 12,
  });

  // Fetch new creators
  const { sequencers: newSequencers, isLoading: newLoading } = useAllSequencers({
    sortBy: 'newest',
    limit: 12,
  });

  // Fetch filtered/searched sequencers
  const {
    sequencers: filteredSequencers,
    isLoading: filteredLoading,
    error: filteredError,
  } = useAllSequencers({
    filterFeatured,
    filterVerified,
    filterFreeOnly,
    holiday: selectedHoliday || undefined,
    software: selectedSoftware || undefined,
    genre: selectedGenre || undefined,
    sortBy: 'sequences',
  });

  // Client-side search filtering
  const searchedSequencers = useMemo(() => {
    if (!searchQuery.trim()) return filteredSequencers;

    const query = searchQuery.toLowerCase();
    return filteredSequencers.filter((seq) => {
      const name = (seq.displayName || seq.username || '').toLowerCase();
      const bio = (seq.bio || '').toLowerCase();
      return name.includes(query) || bio.includes(query);
    });
  }, [searchQuery, filteredSequencers]);

  // Holiday options (common holidays)
  const holidayOptions = [
    'Christmas',
    'Halloween',
    'Easter',
    'Fourth of July',
    'Thanksgiving',
    'Valentines Day',
    'New Years',
  ];

  // Software options
  const softwareOptions = ['xLights', 'LightORama', 'Vixen', 'HLS', 'Falcon Player'];

  // Genre options (top genres)
  const genreOptions = [
    'Christmas',
    'Pop',
    'Rock',
    'Country',
    'Hip-Hop',
    'Classical',
    'Electronic',
  ];

  // Show search results if there are active filters or search
  const hasActiveFilters =
    searchQuery ||
    filterFeatured ||
    filterVerified ||
    filterFreeOnly ||
    selectedHoliday ||
    selectedSoftware ||
    selectedGenre;

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 bg-background">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 pt-24 pb-16 px-4">
        <div className="container max-w-6xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              Discover Amazing
              <span className="block text-primary mt-2">Light Display Creators</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
              Browse talented sequencers who create stunning light displays for holidays and
              events. Find creators by specialty, download count, and more.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search creators by name or bio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg"
              />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {/* Total Creators */}
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary">
                  {statsLoading ? '...' : stats.totalCreators}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Total Creators</div>
              </CardContent>
            </Card>

            {/* Total Sequences */}
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary">
                  {statsLoading ? '...' : stats.totalSequences.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Total Sequences</div>
              </CardContent>
            </Card>

            {/* Combined Downloads */}
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary">
                  {statsLoading ? '...' : stats.totalDownloads.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Total Downloads</div>
              </CardContent>
            </Card>

            {/* Featured Creators */}
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary">
                  {statsLoading ? '...' : stats.featuredCreators}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Featured Creators</div>
              </CardContent>
            </Card>

            {/* Average Sequences */}
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary">
                  {statsLoading ? '...' : stats.avgSequencesPerCreator}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Avg per Creator</div>
              </CardContent>
            </Card>
          </div>

          {/* Filter Toggle Button (Mobile) */}
          <div className="mt-8 text-center md:hidden">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              <ChevronDown
                className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')}
              />
            </Button>
          </div>
        </div>
      </section>

      {/* Filters Section (Desktop: Always visible | Mobile: Collapsible) */}
      <section className={cn('bg-muted/30 border-b', !showFilters && 'md:block hidden')}>
        <div className="container max-w-6xl mx-auto py-6 px-4">
          <div className="grid md:grid-cols-4 gap-6">
            {/* Filter Toggles */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                Quick Filters
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="featured"
                    checked={filterFeatured}
                    onCheckedChange={(checked) => setFilterFeatured(!!checked)}
                  />
                  <Label htmlFor="featured" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-primary" />
                      Featured Only
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="verified"
                    checked={filterVerified}
                    onCheckedChange={(checked) => setFilterVerified(!!checked)}
                  />
                  <Label htmlFor="verified" className="cursor-pointer">
                    Verified Only
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="free-only"
                    checked={filterFreeOnly}
                    onCheckedChange={(checked) => setFilterFreeOnly(!!checked)}
                  />
                  <Label htmlFor="free-only" className="cursor-pointer">
                    Free Sequences Only
                  </Label>
                </div>
              </div>
            </div>

            {/* Holiday Filter */}
            <div className="space-y-2">
              <Label htmlFor="holiday-filter">Holiday Specialty</Label>
              <select
                id="holiday-filter"
                value={selectedHoliday}
                onChange={(e) => setSelectedHoliday(e.target.value)}
                className="w-full border rounded-md p-2 bg-background"
              >
                <option value="">All Holidays</option>
                {holidayOptions.map((holiday) => (
                  <option key={holiday} value={holiday}>
                    {holiday}
                  </option>
                ))}
              </select>
            </div>

            {/* Software Filter */}
            <div className="space-y-2">
              <Label htmlFor="software-filter">Software</Label>
              <select
                id="software-filter"
                value={selectedSoftware}
                onChange={(e) => setSelectedSoftware(e.target.value)}
                className="w-full border rounded-md p-2 bg-background"
              >
                <option value="">All Software</option>
                {softwareOptions.map((software) => (
                  <option key={software} value={software}>
                    {software}
                  </option>
                ))}
              </select>
            </div>

            {/* Genre Filter */}
            <div className="space-y-2">
              <Label htmlFor="genre-filter">Genre</Label>
              <select
                id="genre-filter"
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="w-full border rounded-md p-2 bg-background"
              >
                <option value="">All Genres</option>
                {genreOptions.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="mt-4 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setFilterFeatured(false);
                  setFilterVerified(false);
                  setFilterFreeOnly(false);
                  setSelectedHoliday('');
                  setSelectedSoftware('');
                  setSelectedGenre('');
                }}
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Search Results (if filters/search active) */}
      {hasActiveFilters && (
        <section className="container max-w-6xl mx-auto py-12 px-4">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Search className="h-8 w-8 text-primary" />
              Search Results
            </h2>
            <p className="text-muted-foreground mt-2">
              {filteredLoading
                ? 'Loading...'
                : `Found ${searchedSequencers.length} ${
                    searchedSequencers.length === 1 ? 'creator' : 'creators'
                  }`}
            </p>
          </div>

          {filteredError && (
            <div className="text-center py-12">
              <p className="text-destructive">Error loading creators: {filteredError}</p>
            </div>
          )}

          {!filteredLoading && !filteredError && searchedSequencers.length === 0 && (
            <div className="text-center py-12">
              <User className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No creators found matching your filters. Try adjusting your search.
              </p>
            </div>
          )}

          {!filteredLoading && !filteredError && searchedSequencers.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {searchedSequencers.map((sequencer) => (
                <SequencerCard
                  key={sequencer.id}
                  userId={sequencer.userId}
                  displayName={sequencer.displayName}
                  username={sequencer.username}
                  avatarUrl={sequencer.avatarUrl}
                  bio={sequencer.bio}
                  socialLinks={sequencer.socialLinks}
                  sequenceCount={sequencer.sequenceCount}
                  totalDownloads={sequencer.totalDownloads}
                  averageRating={sequencer.averageRating}
                  freeSequenceCount={sequencer.freeSequenceCount}
                  paidSequenceCount={sequencer.paidSequenceCount}
                  featured={sequencer.featured}
                  verified={sequencer.verified}
                  createdAt={sequencer.createdAt}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Featured Sequencers Section */}
      {!hasActiveFilters && featuredSequencers.length > 0 && (
        <section className="container max-w-6xl mx-auto py-12 px-4">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Award className="h-8 w-8 text-primary" />
              Featured Creators
            </h2>
            <p className="text-muted-foreground mt-2">
              Hand-picked creators showcasing exceptional quality and creativity
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredSequencers.map((sequencer) => (
              <SequencerCard
                key={sequencer.id}
                userId={sequencer.userId}
                displayName={sequencer.displayName}
                username={sequencer.username}
                avatarUrl={sequencer.avatarUrl}
                bio={sequencer.bio}
                socialLinks={sequencer.socialLinks}
                sequenceCount={sequencer.sequenceCount}
                totalDownloads={sequencer.totalDownloads}
                averageRating={sequencer.averageRating}
                freeSequenceCount={sequencer.freeSequenceCount}
                paidSequenceCount={sequencer.paidSequenceCount}
                featured={sequencer.featured}
                verified={sequencer.verified}
                createdAt={sequencer.createdAt}
              />
            ))}
          </div>
        </section>
      )}

      {/* Top Sequencers Section */}
      {!hasActiveFilters && (
        <section className="container max-w-6xl mx-auto py-12 px-4">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <TrendingUp className="h-8 w-8 text-primary" />
              Top Creators
            </h2>
            <p className="text-muted-foreground mt-2">
              Most prolific creators with the highest sequence counts
            </p>
          </div>

          {topLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading top creators...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topSequencers.map((sequencer) => (
                <SequencerCard
                  key={sequencer.id}
                  userId={sequencer.userId}
                  displayName={sequencer.displayName}
                  username={sequencer.username}
                  avatarUrl={sequencer.avatarUrl}
                  bio={sequencer.bio}
                  socialLinks={sequencer.socialLinks}
                  sequenceCount={sequencer.sequenceCount}
                  totalDownloads={sequencer.totalDownloads}
                  averageRating={sequencer.averageRating}
                  freeSequenceCount={sequencer.freeSequenceCount}
                  paidSequenceCount={sequencer.paidSequenceCount}
                  featured={sequencer.featured}
                  verified={sequencer.verified}
                  createdAt={sequencer.createdAt}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Most Downloaded Creators Section */}
      {!hasActiveFilters && (
        <section className="container max-w-6xl mx-auto py-12 px-4 bg-muted/20">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Download className="h-8 w-8 text-primary" />
              Most Downloaded
            </h2>
            <p className="text-muted-foreground mt-2">
              Creators with the most downloads across all their sequences
            </p>
          </div>

          {downloadedLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading popular creators...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {downloadedSequencers.map((sequencer) => (
                <SequencerCard
                  key={sequencer.id}
                  userId={sequencer.userId}
                  displayName={sequencer.displayName}
                  username={sequencer.username}
                  avatarUrl={sequencer.avatarUrl}
                  bio={sequencer.bio}
                  socialLinks={sequencer.socialLinks}
                  sequenceCount={sequencer.sequenceCount}
                  totalDownloads={sequencer.totalDownloads}
                  averageRating={sequencer.averageRating}
                  freeSequenceCount={sequencer.freeSequenceCount}
                  paidSequenceCount={sequencer.paidSequenceCount}
                  featured={sequencer.featured}
                  verified={sequencer.verified}
                  createdAt={sequencer.createdAt}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* New Creators Section */}
      {!hasActiveFilters && (
        <section className="container max-w-6xl mx-auto py-12 px-4">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Clock className="h-8 w-8 text-primary" />
              New Creators
            </h2>
            <p className="text-muted-foreground mt-2">
              Recently joined creators bringing fresh perspectives
            </p>
          </div>

          {newLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading new creators...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {newSequencers.map((sequencer) => (
                <SequencerCard
                  key={sequencer.id}
                  userId={sequencer.userId}
                  displayName={sequencer.displayName}
                  username={sequencer.username}
                  avatarUrl={sequencer.avatarUrl}
                  bio={sequencer.bio}
                  socialLinks={sequencer.socialLinks}
                  sequenceCount={sequencer.sequenceCount}
                  totalDownloads={sequencer.totalDownloads}
                  averageRating={sequencer.averageRating}
                  freeSequenceCount={sequencer.freeSequenceCount}
                  paidSequenceCount={sequencer.paidSequenceCount}
                  featured={sequencer.featured}
                  verified={sequencer.verified}
                  createdAt={sequencer.createdAt}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Browse All CTA */}
      {!hasActiveFilters && (
        <section className="container max-w-6xl mx-auto py-16 px-4 text-center">
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-12">
            <Users className="h-16 w-16 text-primary mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Discover More Creators</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Can't find what you're looking for? Use the filters above to search by holiday,
              software, genre, or browse verified and featured creators.
            </p>
            <Button size="lg" onClick={() => setShowFilters(true)}>
              <Filter className="h-5 w-5 mr-2" />
              Show Filters
            </Button>
          </div>
        </section>
      )}
      </main>
      <Footer />
    </div>
  );
}
